import React, { FC, useMemo } from 'react'
import { Box, Flex, Text, TextLink, Image } from '@moonpig/launchpad-components'
import { useExperiment } from '@moonpig/web-core-eppo/browser'
import { useRouter } from '@moonpig/web-core-routing'
import { useStoreId } from '@moonpig/web-core-stores'
import { POSTCARD_UPSELL_EXCLUDED_SKUS } from '@moonpig/web-core-brand/config'
import { styled } from '@moonpig/launchpad-utils'
import { system as s } from '@moonpig/launchpad-system'
import { useProductStore } from '../../contexts/product'
import { useTracking } from '../../contexts/tracking'
import { POSTCARD_SIZE_ID } from '../../constants'
import { useProductsLocaleText } from '../../locale'
import { buildPostcardUpgradeHref } from '../../utils/buildPostcardUpgradeHref/buildPostcardUpgradeHref'
import { getProductImageDimensions } from '../../utils/getProductImageDimensions'
import { isCardProduct } from '../../utils/isCardProduct'

const EXCLUDED_POSTCARD_UPSELL_SKUS = new Set(POSTCARD_UPSELL_EXCLUDED_SKUS)

const PostcardUpsellContainer = styled(Box)`
  ${s({
    mb: 6,
    bgcolor: 'colorPrimary03',
    p: 6,
    borderRadius: 1,
  })}
`

const StyledCard = styled(Image)`
  transform: skew(0, 5deg);
`

const CardBacking = styled(Box)`
  position: absolute;
  top: -5px;
  left: 0;
  right: 10px;
  bottom: 20px;
  background-color: ${({ theme }) => theme.colors.colorBlack10};
  border-radius: 1px;
`

const StyledTextLink = styled(TextLink)`
  ${s({
    typography: 'typeBodyLabel',
  })}
`

const PostcardUpsellContent: FC<{ upgradeHref: string; imageUrl: string }> = ({
  upgradeHref,
  imageUrl,
}) => {
  const localise = useProductsLocaleText()
  const { selectContent } = useTracking()
  const ctaLabel = localise('postcard_upsell.cta')

  const isPostcardUpsellEnabled =
    useExperiment('cvr-postcard-upsell', { fallback: 'control' }) === 'variant'
  if (!isPostcardUpsellEnabled) {
    return null
  }

  return (
    <PostcardUpsellContainer data-testid="module-postcard-upsell">
      <Flex>
        <Box mr={8} position="relative">
          <CardBacking />
          <StyledCard src={imageUrl} alt="Postcard" width={75} />
        </Box>
        <Flex flexDirection="column">
          <Text typography="typeBodyText">
            {localise('postcard_upsell.description')}
          </Text>
          <Box mt={4}>
            <StyledTextLink
              href={upgradeHref}
              onClick={() =>
                selectContent({
                  action: 'view postcard upgrade',
                  component: 'hyperlink',
                  label: ctaLabel,
                })
              }
            >
              {ctaLabel}
            </StyledTextLink>
          </Box>
        </Flex>
      </Flex>
    </PostcardUpsellContainer>
  )
}

export const PostcardUpsellModule: FC = () => {
  const router = useRouter()
  const storeId = useStoreId()
  const [{ product, variant }] = useProductStore()
  const isPostcard = product.masterVariant.key === POSTCARD_SIZE_ID
  const sku = product.id.toLowerCase()
  const isExcludedSku = EXCLUDED_POSTCARD_UPSELL_SKUS.has(sku)

  const upgradeHref = useMemo(
    () =>
      buildPostcardUpgradeHref({
        router,
        product,
        variant,
        storeId,
      }),
    [router, product, variant, storeId],
  )

  const imageUrl = useMemo(() => {
    const firstUrl = product.masterVariant.productImages.mainImage.medium.jpgUrl

    if (!firstUrl) {
      return undefined
    }

    const { width } = getProductImageDimensions(
      isCardProduct(product.category.department),
      product.masterVariant.key,
      product.isLandscape,
    )

    return `${firstUrl}?w=${width}&productSizeId=${variant.key}`
  }, [product, variant])

  if (!isPostcard || isExcludedSku || !upgradeHref || !imageUrl) {
    return null
  }

  return <PostcardUpsellContent upgradeHref={upgradeHref} imageUrl={imageUrl} />
}
